import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import { useTranslation } from "../../hooks";
import { getFilledBookingFormEntries } from "./utils/bookingFormValidation";
import { convertISTToTimezone, formatDateToISO, calculateDuration, } from "./utils/date";
import { chargeRow, unquotedChargeRow, } from "./utils/chargeRow";
const DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
export function SummaryStep({ state, config }) {
    const { t } = useTranslation("appointment");
    const { selectedDoctor, selectedSlot, selectedDate, selectedTimezone, consultationMode, selectedPatient, selectedPack, newPatientForm, patientTab, countryCode, patientPhone, bookingError, bookingFormSchema, bookingFormValues, quote, pricePending = false, selectedDoctorDisplay, } = state;
    const additionalDetails = getFilledBookingFormEntries(bookingFormSchema, bookingFormValues);
    const cardTitle = config?.summaryCardTitle ?? t("appointmentDetails");
    const showFee = config?.showFeeInSummary ?? true;
    const slotTimeFormat = config?.slotTimeFormat ?? "start";
    const showDuration = config?.showDuration ?? true;
    const patientName = patientTab === "existing" && selectedPatient
        ? [selectedPatient.firstName, selectedPatient.lastName]
            .filter(Boolean)
            .join(" ")
        : newPatientForm.fullName;
    const patientEmail = patientTab === "existing" && selectedPatient
        ? selectedPatient.email || ""
        : newPatientForm.email || "";
    const timeDisplay = useMemo(() => {
        if (!selectedSlot)
            return null;
        const dateStr = formatDateToISO(selectedDate);
        const startStr = selectedSlot.start.includes("T")
            ? selectedSlot.start.split("T")[1].substring(0, 5)
            : selectedSlot.start.substring(0, 5);
        const displayStart = convertISTToTimezone(startStr, dateStr, selectedTimezone);
        const day = DAYS[selectedDate.getDay()];
        const month = MONTHS[selectedDate.getMonth()];
        const date = selectedDate.getDate();
        const mode = consultationMode === "ONLINE" ? t("online") : t("inPerson");
        if (slotTimeFormat === "range") {
            const endStr = selectedSlot.end.includes("T")
                ? selectedSlot.end.split("T")[1].substring(0, 5)
                : selectedSlot.end.substring(0, 5);
            const displayEnd = convertISTToTimezone(endStr, dateStr, selectedTimezone);
            return {
                dateStr: `${day}, ${month} ${date}`,
                time: `${displayStart} – ${displayEnd}`,
                mode,
                duration: null,
            };
        }
        const duration = calculateDuration(selectedSlot.start, selectedSlot.end, 15);
        return {
            dateStr: `${day}, ${month} ${date}`,
            time: displayStart,
            mode,
            duration: t("minutes", undefined, { count: duration }),
        };
    }, [
        selectedSlot,
        selectedDate,
        selectedTimezone,
        consultationMode,
        slotTimeFormat,
        t,
    ]);
    const feeDisplay = useMemo(() => {
        if (!selectedDoctor || !showFee)
            return null;
        const row = quote ? chargeRow(quote, t) : unquotedChargeRow(selectedPack, t);
        return pricePending && quote?.kind !== "ACTIVE_PACK"
            ? { ...row, value: "—" }
            : row;
    }, [selectedDoctor, showFee, quote, pricePending, selectedPack, t]);
    return (_jsx(Box, { sx: {
            p: { xs: 2.5, sm: "28px 24px" },
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
        }, children: _jsxs(Box, { sx: { maxWidth: 680, mx: "auto" }, children: [_jsxs(Box, { sx: {
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                        mb: 2,
                    }, children: [_jsxs(Card, { variant: "outlined", sx: { borderRadius: 2, overflow: "hidden" }, children: [_jsx(Box, { sx: {
                                        px: 2,
                                        py: 1.25,
                                        borderBottom: 1,
                                        borderColor: "divider",
                                        bgcolor: "rgba(21,59,98,0.04)",
                                    }, children: _jsx(Typography, { variant: "subtitle2", sx: { fontWeight: 700 }, children: t("patientDetails") }) }), _jsxs(CardContent, { sx: { px: 2, py: 1.5, pb: "14px !important" }, children: [_jsx(Row, { label: t("name"), value: patientName || "—" }), _jsx(Row, { label: t("phone"), value: `${countryCode} ${patientPhone}` }), patientEmail && _jsx(Row, { label: t("email"), value: patientEmail }), patientTab === "existing" && selectedPatient && (_jsxs(_Fragment, { children: [selectedPatient.gender && (_jsx(Row, { label: t("gender"), value: selectedPatient.gender })), Boolean(selectedPatient.age) && (_jsx(Row, { label: t("age"), value: t("years", undefined, {
                                                        count: selectedPatient.age,
                                                    }) }))] })), patientTab === "new" && (_jsxs(_Fragment, { children: [newPatientForm.gender && (_jsx(Row, { label: t("gender"), value: newPatientForm.gender })), Boolean(newPatientForm.age) && (_jsx(Row, { label: t("age"), value: t("years", undefined, {
                                                        count: newPatientForm.age,
                                                    }) })), newPatientForm.city && (_jsx(Row, { label: t("city"), value: newPatientForm.city }))] }))] })] }), _jsxs(Card, { variant: "outlined", sx: { borderRadius: 2, overflow: "hidden" }, children: [_jsx(Box, { sx: {
                                        px: 2,
                                        py: 1.25,
                                        borderBottom: 1,
                                        borderColor: "divider",
                                        bgcolor: "rgba(21,59,98,0.04)",
                                    }, children: _jsx(Typography, { variant: "subtitle2", sx: { fontWeight: 700 }, children: cardTitle }) }), _jsxs(CardContent, { sx: { px: 2, py: 1.5, pb: "14px !important" }, children: [selectedDoctorDisplay?.name && (_jsx(Row, { label: t("doctor"), value: selectedDoctorDisplay.name, accent: true })), timeDisplay && (_jsxs(_Fragment, { children: [_jsx(Row, { label: t("date"), value: timeDisplay.dateStr }), _jsx(Row, { label: t("time"), value: timeDisplay.time }), _jsx(Row, { label: t("mode"), value: timeDisplay.mode, badge: true }), showDuration && timeDisplay.duration && (_jsx(Row, { label: t("duration"), value: timeDisplay.duration })), feeDisplay && (_jsxs(_Fragment, { children: [_jsx(Row, { label: feeDisplay.label, value: feeDisplay.value, bold: true }), feeDisplay.caption && (_jsx(Row, { label: "", value: feeDisplay.caption, muted: true }))] }))] }))] })] }), additionalDetails.length > 0 && (_jsxs(Card, { variant: "outlined", sx: { borderRadius: 2, overflow: "hidden" }, children: [_jsx(Box, { sx: {
                                        px: 2,
                                        py: 1.25,
                                        borderBottom: 1,
                                        borderColor: "divider",
                                        bgcolor: "rgba(21,59,98,0.04)",
                                    }, children: _jsx(Typography, { variant: "subtitle2", sx: { fontWeight: 700 }, children: "Additional Details" }) }), _jsx(CardContent, { sx: { px: 2, py: 1.5, pb: "14px !important" }, children: additionalDetails.map((entry) => (_jsx(Row, { label: entry.label, value: entry.value }, entry.label))) })] }))] }), bookingError && (_jsx(Alert, { severity: "error", sx: { mt: 1.5 }, children: bookingError }))] }) }));
}
function Row({ label, value, bold, accent, badge, muted, }) {
    return (_jsxs(Box, { sx: {
            display: "grid",
            gridTemplateColumns: "minmax(92px, 40%) 1fr",
            alignItems: "start",
            columnGap: 1.25,
            py: muted ? 0.25 : 0.625,
        }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: label }), badge ? (_jsx(Chip, { label: value, size: "small", variant: "outlined", sx: { fontWeight: 600, fontSize: 12, justifySelf: "end" } })) : (_jsx(Typography, { variant: muted ? "caption" : "body2", sx: {
                    fontWeight: muted ? 500 : bold ? 700 : 600,
                    color: muted
                        ? "success.main"
                        : accent
                            ? "primary.main"
                            : "text.primary",
                    textAlign: "right",
                    overflowWrap: "anywhere",
                }, children: value }))] }));
}
